import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { StoreProvider, useAppState } from './app/context.js';
import { FocusManager } from './app/focus.js';
import { createAppStore } from './app/createStore.js';
import { AuthScreen } from './auth/AuthScreen.js';
import { ColourStep } from './auth/ColourStep.js';
import type { AuthSource, DataSource } from './contract.js';
import { PickersProvider } from './app/pickers.js';
import { Hub } from './island/Hub.js';
import { Island } from './island/Island.js';
import { dayVals } from './select/day.js';
import { hubVals } from './select/hub.js';
import { personVals } from './select/person.js';
import { surfaceVals } from './select/surface.js';
import { useReducedMotion } from './stage/motion.js';
import { useTouchDevice } from './stage/pointer.js';
import { Stage } from './stage/Stage.js';
import type { AppStore } from './state/store.js';
import { Boot } from './views/Boot.js';
import { CalendarLayer } from './views/CalendarLayer.js';
import { DayLayer } from './views/DayLayer.js';
import { Header } from './views/Header.js';
import { ProfileHeader } from './views/ProfileHeader.js';
import { Surface } from './views/Surface.js';
import { Toast } from './views/Toast.js';

export interface V1AppProps {
  /** Where the data comes from: the fixture source in development, the backend source in production. */
  readonly data: DataSource;
  /** Signing in and up. With it, the sign-in screen shows when nobody is signed in (06 §5.7). */
  readonly auth?: AuthSource;
  /** Shown instead when there is no `auth` and nobody is signed in. */
  readonly signedOut?: ReactNode;
  /** A password-reset link was opened: its token. The reset screen shows first, whoever is signed in. */
  readonly resetToken?: string | null;
  /** The reset link was used or given up; the address can be cleaned. */
  readonly onResetDone?: () => void;
  /**
   * A sign-in that failed on its way back from Google: the fixed sentence for it. The sign-in screen
   * opens with it once; it is dropped when somebody turns out to be signed in. Read at the first render only.
   */
  readonly signInError?: string | null;
  /** Receives the store once, for tests and the development tools. */
  readonly onStore?: (store: AppStore) => void;
}

/** The v1 UI: one fixed stage, every view in the calendar's footprint, one island at the bottom. */
export function V1App({ data, auth, signedOut = null, resetToken = null, onResetDone, signInError = null, onStore }: V1AppProps) {
  const [store] = useState(() => createAppStore(data));
  const [resetting, setResetting] = useState(!!resetToken && !!auth);
  // Somebody who signed in on this page and is asked again (the session did not hold) came to sign in.
  const [tried, setTried] = useState(false);
  useEffect(() => {
    onStore?.(store);
    void store.start();
    return () => store.dispose();
  }, [store, onStore]);
  useEffect(() => {
    // Today can change while the app is open: re-read it when the page shows again and at local midnight (06 §5.1).
    const refresh = () => { if (document.visibilityState === 'visible' && store.getState().boot === 'ready') void store.refreshSession(); };
    let timer: ReturnType<typeof setTimeout>;
    const arm = () => {
      const now = new Date(), next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1);
      timer = setTimeout(() => { refresh(); arm(); }, next.getTime() - now.getTime());
    };
    arm();
    document.addEventListener('visibilitychange', refresh);
    return () => { clearTimeout(timer); document.removeEventListener('visibilitychange', refresh); };
  }, [store]);
  // Finishing a reset ends whatever session this device had: the new password is what signs in from now on.
  const resetDone = () => { setResetting(false); onResetDone?.(); if (store.getState().boot !== 'signed-out') store.signedOut(); };
  // Leaving an account that has no profile yet: whatever the backend answers, this device shows sign-in again.
  const leave = () => { void data.signOut().catch(() => undefined).finally(() => store.signedOut()); };
  const gate = (finishing: boolean, initialError: string | null, returning: boolean) => auth
    ? <AuthScreen key={finishing ? 'finish' : 'auth'} auth={auth} resetToken={resetting ? resetToken : null} finishing={finishing} onSignOut={leave}
      onSignedIn={() => { setTried(true); void store.start(); }} onResetDone={resetDone} initialError={initialError} initialMode={returning || tried || initialError ? 'signIn' : 'signUp'} />
    : signedOut;
  return <StoreProvider value={store}><Stage><Screen gate={gate} resetting={resetting} leave={leave} signInError={signInError} /></Stage></StoreProvider>;
}

function Screen({ gate, resetting, leave, signInError }: {
  readonly gate: (finishing: boolean, initialError: string | null, returning: boolean) => ReactNode; readonly resetting: boolean; readonly leave: () => void; readonly signInError: string | null;
}) {
  const st = useAppState();
  // Somebody who was signed in on this page and no longer is came to sign in again, not to create an account
  // (owner, 4 October 2026: Create account is what a new visitor sees first).
  const [returning, setReturning] = useState(false);
  const inside = st.boot === 'ready' || st.boot === 'choose-colour' || st.boot === 'needs-profile';
  useEffect(() => { if (inside) setReturning(true); }, [inside]);
  // The returned sign-in failure belongs to the first answer about the session: signed out, the screen that
  // mounts then keeps it as its own error; signed in, nobody needs it. Either way it is not handed over twice.
  const [returned, setReturned] = useState(signInError);
  const known = st.boot !== 'loading' && st.boot !== 'error';
  useEffect(() => { if (known) setReturned(null); }, [known]);
  if (st.boot === 'signed-out' || st.boot === 'needs-profile' || resetting) return <>{gate(st.boot === 'needs-profile' && !resetting, st.boot === 'signed-out' ? returned : null, returning)}</>;
  if (st.boot === 'choose-colour') return <ColourStep onSignOut={leave} />;
  if (st.boot !== 'ready') return <Boot failed={st.boot === 'error'} />;
  return <PickersProvider><Views /></PickersProvider>;
}

/**
 * Every layer of the stage. Their order here is the Tab order (title row, grid, centre controls,
 * step-up, island; 03 §5.2); which one is drawn on top is set by each layer's z-index (02 §1.10).
 */
function Views() {
  const st = useAppState(), reduced = useReducedMotion(), touch = useTouchDevice();
  const day = dayVals(st, reduced), person = personVals(st, day, reduced);
  return <>
    <Header />
    <ProfileHeader />
    <CalendarLayer />
    <DayLayer day={day} />
    <Surface sf={surfaceVals(st, person)} />
    <Hub hub={hubVals(st, person, { camera: touch })} />
    <Island />
    <Toast />
    <FocusManager />
  </>;
}
